import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  FiltersBar,
  Icon,
  IconButton,
  Input,
  PageHeader,
  PageLayout,
  SearchInput,
  Segmented,
  StatePanel,
  Tabs,
} from "../../components/ui";
import {
  fetchErrorGroups,
  fetchErrorGroup,
  updateErrorStatus,
  updateHumanFlag,
} from "./api.js";
import { UserContext } from "../../contexts";
import BetaReportDetail, { BetaToggle } from "./BetaReportDetail.jsx";
import IncidentLog from "./IncidentLog.jsx";
import { AUTO_FIX_PAUSED } from "./autoFix.js";
import "./monitoring.scss";

// The "Allure monitoring" header link (http://3.10.208.169:8081) is
// deliberately gone.
//
// That dashboard is static JSON regenerated by a cron switched off on
// 2026-06-16, so nginx still serves the June run with an HTTP 200 and
// confident-looking numbers. Offering it beside live errors invited people to
// read a frozen snapshot as current state.
//
// Put it back when the cron is running again — and show the age of the data
// next to it when you do. Until then, no link beats a link to stale numbers.

const APP_LABEL = { dao: "DAO Watches", rc: "Retail Circle", api: "API" };
const APP_TONE = { dao: "warning", rc: "accent", api: "info" };
// State pills use a dot and colours the Where column never uses (it is red for
// prod, grey for staging), so the two read apart at a glance (Alex, 2026-10-14).
const STATE_TONE = { open: "warning", resolved: "success", verified: "success", ignored: "info" };

const APP_FILTERS = [
  { value: "", label: "All apps" },
  { value: "dao", label: "DAO Watches" },
  { value: "rc", label: "Retail Circle" },
  { value: "api", label: "API" },
];
// Error groups (captured automatically) and beta reports (sent by a person
// from the beta button) share this board; the API merges them by recency.
const SOURCE_FILTERS = [
  { value: "all", label: "Everything" },
  { value: "error", label: "Errors" },
  { value: "beta", label: "Beta reports" },
];
const SOURCE_LABEL = { error: "Error", beta: "Beta report" };
const SOURCE_TONE = { error: "muted", beta: "accent" };
const sourceOf = (row) => row.source || "error";
const rowKeyOf = (row) => `${sourceOf(row)}-${row.id}`;

const STATE_FILTERS = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "ignored", label: "Ignored" },
  { value: "all", label: "All" },
];

const relTime = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
};

const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : s);

const DAY_MS = 86400000;
const daysSince = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / DAY_MS) : null);
const hoursSince = (iso) => (iso ? (Date.now() - new Date(iso).getTime()) / 3600000 : null);

/**
 * Which triage bucket a group belongs in.
 *
 * A single list ordered by last_seen_at cannot answer "what should I look at
 * first" — with 1110 open groups it is a wall, and the loudest row is usually
 * the least important. These three questions are what an on-call person
 * actually has:
 *
 *   onFire   people are blocked, right now
 *   fresh    this started recently — most likely the last deploy
 *   quiet    open for over a month and STILL arriving
 *
 * The third is the one nothing has ever surfaced here, and it is where every
 * expensive incident at this company has lived: Gemini NULL for three months,
 * backups missing for a year. They were never the noisiest row, so they were
 * never the top of a list sorted by recency.
 *
 * Computed from fields the API already returns — no backend change needed.
 */
const bucketOf = (row) => {
  const state = row.effective_state || row.state;
  if (state !== "open") return "handled";
  // A person took the trouble to report it — that outranks every heuristic.
  if (sourceOf(row) === "beta") return "reported";

  const seenRecently = (hoursSince(row.last_seen_at) ?? 1e9) <= 24;
  const age = daysSince(row.first_seen_at) ?? 0;

  if (seenRecently && (row.users_affected || 0) > 0) return "onFire";
  if (age > 30 && seenRecently) return "quiet";
  if (age <= 7) return "fresh";
  return "other";
};

const BUCKETS = [
  {
    key: "reported",
    title: "Reported by people",
    why: "open · sent from the beta button — screenshot, logs and any spoken note",
    tone: "accent",
  },
  {
    key: "onFire",
    title: "Affecting users now",
    why: "open · someone is blocked · seen in the last 24h",
    tone: "danger",
  },
  {
    key: "fresh",
    title: "Started recently",
    why: "first seen in the last 7 days — most likely the last deploy",
    tone: "info",
  },
  {
    key: "quiet",
    title: "Quietly broken for weeks",
    why: "open over a month and still arriving — nothing has ever surfaced these",
    tone: "warning",
  },
  { key: "other", title: "Older, nobody blocked", why: "open, but no users affected recently", tone: "muted" },
  { key: "handled", title: "Handled", why: "resolved, verified or deliberately ignored", tone: "success" },
];

const StateBadge = ({ state }) => (
  <Badge tone={STATE_TONE[state] || "info"} size="sm" dot>
    {cap(state)}
  </Badge>
);

const AppBadge = ({ app }) => (
  <Badge tone={APP_TONE[app] || "muted"} size="sm">
    {APP_LABEL[app] || app}
  </Badge>
);

// One board (Alex, 2026-10-06): the Errors / Beta reports tabs are merged, with
// a Source column and filter, and the "What we checked" tab is removed (it did
// not work).
const VIEWS = [
  { value: "board", label: "Live board" },
  { value: "incidents", label: "Incident log" },
];

const MonitoringPage = () => {
  const { user } = useContext(UserContext) || {};
  const [view, setView] = useState("board");
  const [source, setSource] = useState("all");
  const [app, setApp] = useState("");
  const [state, setState] = useState("open");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");

  const [summary, setSummary] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // { source, id } — an error group and a beta report can share an id.
  const [selected, setSelected] = useState(null);
  const selectedId = selected?.source === "error" ? selected.id : null;
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const load = useCallback(async (opts) => {
    // A background refresh (after Resolve / Ignore / the human flag) must not
    // swap the list for "Loading…": that unmounts every bucket, jumps the
    // scroll to the top and reads as the page reloading under the reader.
    // Only a new view (first load, filter, page) shows the loading state.
    const background = opts?.background === true;

    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      const params = { state, page, source };
      if (app) params.app = app;
      if (debouncedQ) params.q = debouncedQ;
      const data = await fetchErrorGroups(params);
      setSummary(data.summary || null);
      // groups is a Laravel paginator → rows live under .data
      setGroups(data.groups?.data || data.groups || []);
      // Keep the envelope so the pager knows how many pages exist. It was
      // always in the payload; nothing read it, so 1110 groups were shown 30
      // at a time with no way to reach the rest.
      setPagination(
        data.groups?.last_page
          ? {
              current_page: data.groups.current_page,
              last_page: data.groups.last_page,
              total: data.groups.total,
            }
          : null
      );
    } catch (e) {
      // A failed background refresh keeps what is on screen; the next
      // explicit load surfaces the error.
      if (background) return;
      setError(
        e?.response?.status === 403
          ? "Monitoring is restricted to master admins."
          : "Couldn't load errors. Please try again."
      );
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [app, state, debouncedQ, page, source]);

  // A filter change must reset paging. Without this, switching app while on
  // page 7 requests a page that no longer exists and the board reads as
  // "Nothing here" — an empty result that looks like good news.
  useEffect(() => {
    setPage(1);
  }, [app, state, debouncedQ, source]);

  useEffect(() => {
    load();
  }, [load]);

  /** Rows split into triage buckets. See bucketOf() for why. */
  const grouped = useMemo(() => {
    const out = {};
    for (const row of groups) {
      const key = bucketOf(row);
      (out[key] = out[key] || []).push(row);
    }
    // Within a bucket, most people affected first — then most recent.
    for (const key of Object.keys(out)) {
      out[key].sort(
        (a, b) =>
          (b.users_affected || 0) - (a.users_affected || 0) ||
          new Date(b.last_seen_at || 0) - new Date(a.last_seen_at || 0)
      );
    }
    return out;
  }, [groups]);

  const openRow = (row) => {
    if (sourceOf(row) === "beta") {
      // The beta panel loads its own report.
      setSelected({ source: "beta", id: row.id });
      setDetail(null);
      return;
    }
    openDetail(row.id);
  };

  const openDetail = useCallback(async (id) => {
    setSelected({ source: "error", id });
    setDetail(null);
    setDetailLoading(true);
    try {
      const data = await fetchErrorGroup(id);
      setDetail(data);
    } catch (_) {
      setDetail({ error: true });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => {
    setSelected(null);
    setDetail(null);
  };

  // Turning the human flag off grants the automation authority to write and
  // push a fix for this error. Refresh both the detail and the list so the
  // change is visible immediately rather than on the next poll.
  const applyFlag = async (id, requiresHuman) => {
    try {
      const data = await updateHumanFlag(id, requiresHuman);
      setDetail((d) => (d ? { ...d, group: data.group } : d));
      load({ background: true });
    } catch (_) {
      /* best-effort; the checkbox reverts on the next load */
    }
  };

  const applyStatus = async (nextState, extra = {}) => {
    if (!selectedId) return;
    try {
      const data = await updateErrorStatus(selectedId, { state: nextState, ...extra });
      // Refresh the detail + the list (the row may drop out of the filter).
      setDetail((d) => (d ? { ...d, group: data.group } : d));
      load({ background: true });
    } catch (_) {
      /* best-effort; inline state already shown */
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "source",
        header: "Source",
        cardLabel: "Source",
        cell: (row) => (
          <Badge tone={SOURCE_TONE[sourceOf(row)] || "muted"} size="sm">
            {SOURCE_LABEL[sourceOf(row)] || sourceOf(row)}
          </Badge>
        ),
      },
      {
        key: "app",
        header: "App",
        card: "aside",
        cell: (row) => <AppBadge app={row.app} />,
      },
      {
        key: "error",
        header: "Issue",
        card: "title",
        // Message only (Alex, 2026-10-08) — the type ("window_error",
        // "Beta report"…) is in the Source column, the hover title and the
        // detail panel, and stays searchable.
        cell: (row) => (
          <span className="mon-page__error-cell" title={row.type || undefined}>
            <span className="mon-page__error-msg">{row.title}</span>
          </span>
        ),
      },
      {
        key: "page",
        header: "Page",
        card: "subtitle",
        cell: (row) => (
          <span className="mon-page__page-cell" title={row.page}>
            {row.page || "—"}
          </span>
        ),
      },
      // People before occurrences, deliberately. 8,917 occurrences hitting
      // nobody ranks below 11 people blocked, and putting Count first taught
      // the opposite. A zero reads muted rather than as a number to compare.
      {
        key: "users_affected",
        header: "People",
        align: "end",
        cardLabel: "People affected",
        cell: (row) =>
          row.users_affected > 0 ? (
            <strong className="mon-page__people">{row.users_affected}</strong>
          ) : (
            <span className="mon-page__people mon-page__people--none">—</span>
          ),
      },
      { key: "event_count", header: "Times", align: "end", cardLabel: "Occurrences" },
      {
        key: "environments",
        header: "Where",
        cell: (row) => {
          // environments_affected comes from the environments_seen map: the
          // single `environment` column only holds the LAST occurrence, so it
          // flips between staging and production and cannot answer "is this
          // live in prod". Falls back gracefully on older API responses.
          const envs = row.environments_affected?.length
            ? row.environments_affected
            : [row.environment].filter(Boolean);

          return (
            <span className="mon-page__envs">
              {envs.map((e) => (
                <Badge key={e} tone={e === "production" ? "danger" : "muted"} size="sm">
                  {e === "production" ? "prod" : e}
                </Badge>
              ))}
            </span>
          );
        },
      },
      {
        key: "last_seen_at",
        header: "Last seen",
        cell: (row) => <span title={row.last_seen_at}>{relTime(row.last_seen_at)}</span>,
      },
      {
        key: "state",
        header: "State",
        card: "aside",
        cell: (row) => <StateBadge state={row.effective_state || row.state} />,
      },
    ],
    []
  );

  // "Reported by people" also says WHO reported it — right after the issue.
  const reportedColumns = useMemo(() => {
    const userColumn = {
      key: "user",
      header: "User",
      cardLabel: "Reported by",
      cell: (row) => <span className="mon-page__user-cell">{row.user?.name || "—"}</span>,
    };
    const at = columns.findIndex((c) => c.key === "error") + 1;
    return [...columns.slice(0, at), userColumn, ...columns.slice(at)];
  }, [columns]);

  const tableState = loading ? "loading" : error ? "error" : groups.length === 0 ? "no-results" : undefined;
  const stateProps = useMemo(() => {
    if (error) return { title: error, description: null, onRetry: load };
    if (!loading && groups.length === 0) {
      return { title: "Nothing here", description: "Nothing matches this view. 🎉" };
    }
    return undefined;
  }, [error, loading, groups.length, load]);

  return (
    <PageLayout className="mon-page">
      <PageHeader
        title="Monitoring"
        description="Real errors from daowatches.com, app.retail-circle.io and the API — grouped, with the page and interaction that triggered them — and the reports people send from the beta button."
        showBackButton={false}
        actions={user?.is_master_admin ? <BetaToggle /> : []}
      />

      {/* The live board, and the incident memory built from it (Alex, 2026-10-14). */}
      <Tabs items={VIEWS} value={view} onChange={setView} variant="underline" aria-label="Monitoring views" className="mon-page__tabs" />

      {view === "board" && summary && (
        <div className="mon-page__summary">
          <Card padding="sm" className="mon-page__stat mon-page__stat--open">
            <span className="mon-page__stat-num">{summary.open}</span>
            <span className="mon-page__stat-label">Open errors</span>
          </Card>
          {summary.beta_open != null && (
            <Card padding="sm" className="mon-page__stat mon-page__stat--open">
              <span className="mon-page__stat-num">{summary.beta_open}</span>
              <span className="mon-page__stat-label">Open beta reports</span>
            </Card>
          )}
          <Card padding="sm" className="mon-page__stat">
            <span className="mon-page__stat-num">{summary.by_app?.dao ?? 0}</span>
            <span className="mon-page__stat-label">DAO Watches</span>
          </Card>
          <Card padding="sm" className="mon-page__stat">
            <span className="mon-page__stat-num">{summary.by_app?.rc ?? 0}</span>
            <span className="mon-page__stat-label">Retail Circle</span>
          </Card>
          <Card padding="sm" className="mon-page__stat">
            <span className="mon-page__stat-num">{summary.by_app?.api ?? 0}</span>
            <span className="mon-page__stat-label">API</span>
          </Card>
          <Card padding="sm" className="mon-page__stat mon-page__stat--muted">
            <span className="mon-page__stat-num">{summary.resolved}</span>
            <span className="mon-page__stat-label">Resolved</span>
          </Card>
        </div>
      )}

      {view === "board" && (
      <>
          <FiltersBar
            label="Filter the board"
            search={
              <SearchInput
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onSearch={setDebouncedQ}
                placeholder="Search title, page, type, note…"
              />
            }
          >
            <Segmented options={SOURCE_FILTERS} value={source} onChange={setSource} label="Filter by source" />
            <Segmented options={APP_FILTERS} value={app} onChange={setApp} label="Filter by app" />
            <Segmented options={STATE_FILTERS} value={state} onChange={setState} label="Filter by status" />
          </FiltersBar>

          <div className={`mon-page__body ${selected ? "mon-page__body--split" : ""}`}>
            {/*
              The list is wrapped in ONE element on purpose.

              --split is a two-column grid: list | detail. Every direct child
              of it becomes a grid item, so when the flat table was replaced
              by five bucket sections plus a pager, they tiled themselves
              across both columns — "Affecting users now" in the left column,
              "Started recently" in the right, and the detail panel pushed to
              the bottom. Reported 2026-09-10 with screenshots.

              Keeping exactly two children is what makes the grid mean what
              it says.
            */}
            <div className="mon-page__list">
            {/*
              Grouped, not one flat list.

              With 1110 open groups a single table ordered by last_seen_at is a
              wall, and the loudest row is rarely the one that matters. Each
              section states the rule that put rows in it, so the ordering is
              explainable rather than something to be trusted blindly.

              Loading, error and empty states still render through a single table
              so those paths — and the tests covering them — are unchanged.
            */}
            {tableState ? (
              <DataTable
                caption="Error groups"
                columns={columns}
                rows={[]}
                rowKey="id"
                state={tableState}
                stateProps={stateProps}
              />
            ) : (
              BUCKETS.map(({ key, title, why, tone }) => {
                const rows = grouped[key] || [];
                if (!rows.length) return null;

                return (
                  <section key={key} className={`mon-page__bucket mon-page__bucket--${key}`}>
                    <header className="mon-page__bucket-head">
                      <Badge tone={tone} size="sm">
                        {rows.length}
                      </Badge>
                      <h2 className="mon-page__bucket-title">{title}</h2>
                      <span className="mon-page__bucket-why">{why}</span>
                    </header>

                    <DataTable
                      caption={title}
                      columns={key === "reported" ? reportedColumns : columns}
                      rows={rows}
                      rowKey={rowKeyOf}
                      onRowClick={openRow}
                      rowActionLabel={(row) => (sourceOf(row) === "beta" ? "Open beta report" : "Open error details")}
                      rowProps={(row) =>
                        selected && rowKeyOf(row) === `${selected.source}-${selected.id}`
                          ? { className: "mon-page__row--active" }
                          : {}
                      }
                    />
                  </section>
                );
              })
            )}

            {/*
              1110 open groups were being shown 30 at a time with no way forward —
              everything past the first page was unreachable, and the paginator was
              in the payload all along.
            */}
            {!tableState && pagination && pagination.last_page > 1 && (
              <nav className="mon-page__pager" aria-label="Error pages">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="mon-page__pager-info">
                  Page {pagination.current_page} of {pagination.last_page}
                  {" · "}
                  {pagination.total} {source === "beta" ? "reports" : source === "all" ? "items" : "groups"}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page >= pagination.last_page}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </nav>
            )}
            </div>

            {selectedId && (
              <ErrorDetail
                loading={detailLoading}
                detail={detail}
                onClose={closeDetail}
                onResolve={(note, release) =>
                  applyStatus("resolved", { note, resolved_release: release })
                }
                onIgnore={() => applyStatus("ignored")}
                onReopen={() => applyStatus("open")}
            onFlagChange={applyFlag}
              />
            )}
            {selected?.source === "beta" && (
              <BetaReportDetail
                key={selected.id}
                id={selected.id}
                onClose={closeDetail}
                onChanged={() => load({ background: true })}
              />
            )}
          </div>
      </>
      )}
      {view === "incidents" && <IncidentLog />}
    </PageLayout>
  );
};

/**
 * What the automation did to this error group, and what a person does next.
 *
 * Every state names an ACTION, not a status. "merged_staging" is useless on
 * its own; "verify on staging, then promote" is what the reader needs. There
 * is deliberately no success state — the trail ends where a person takes over,
 * because releasing to production is a human act.
 */
const AUTO_FIX = {
  proposed: {
    tone: "info",
    label: "Claude has prepared a fix",
    action: "Review the proposed patch below. Nothing has been pushed.",
  },
  pr_open: {
    tone: "info",
    label: "Claude opened a pull request",
    action: "Tests passed. Review the PR and merge it if you agree.",
  },
  merged_staging: {
    tone: "warning",
    label: "Claude merged a fix to staging",
    action:
      "This is live on STAGING now. Verify it there, then promote to production yourself — nothing reaches production automatically.",
  },
  blocked: {
    tone: "danger",
    label: "Claude tried to fix this and stopped",
    action: "Read why below, then take it from here.",
  },
};

const AutoFixBanner = ({ group }) => {
  const state = AUTO_FIX[group.auto_fix_state];

  if (!state) return null;

  return (
    <Card padding="sm" className={`mon-page__autofix mon-page__autofix--${group.auto_fix_state}`}>
      <div className="mon-page__autofix-head">
        <Badge tone={state.tone} size="sm">
          Automated
        </Badge>
        <strong>{state.label}</strong>
        {group.auto_fix_at && (
          <span className="mon-page__autofix-when">{relTime(group.auto_fix_at)}</span>
        )}
      </div>

      <p className="mon-page__autofix-action">{state.action}</p>

      {group.auto_fix_url && (
        <a
          className="mon-page__autofix-link"
          href={group.auto_fix_url}
          target="_blank"
          rel="noreferrer noopener"
        >
          Open the pull request →
        </a>
      )}

      {group.auto_fix_note && <pre className="mon-page__autofix-note">{group.auto_fix_note}</pre>}
    </Card>
  );
};

const VERDICT_LABEL = {
  needs_human: "Diagnosed — needs a person",
  no_finding: "Looked, found nothing useful",
  fixed: "Claude ran a repair",
};
const VERDICT_TONE = { needs_human: "info", no_finding: "muted", fixed: "success" };

/**
 * Claude's diagnosis, and the human flag.
 *
 * 125 diagnoses were written before any of this was visible — reading one
 * needed a shell script on the production box. A diagnosis nobody can see may
 * as well not exist, and it made the feature impossible to judge.
 */
const TriagePanel = ({ group, onFlagChange }) => {
  const [open, setOpen] = useState(false);

  // Nothing has looked at this yet. Say so plainly rather than rendering an
  // empty box that reads as "nothing to report".
  if (!group.triaged_at) {
    return (
      <Card padding="sm" className="mon-page__triage mon-page__triage--none">
        <span className="mon-page__triage-empty">Not yet triaged.</span>
      </Card>
    );
  }

  const requiresHuman = group.requires_human !== false;

  return (
    <Card padding="sm" className="mon-page__triage">
      <div className="mon-page__triage-head">
        <Badge tone={VERDICT_TONE[group.triaged_verdict] || "muted"} size="sm">
          {VERDICT_LABEL[group.triaged_verdict] || group.triaged_verdict}
        </Badge>
        {group.triaged_confidence && (
          <span className="mon-page__triage-conf">{group.triaged_confidence} confidence</span>
        )}
        {group.triaged_model && (
          <span className="mon-page__triage-model">{group.triaged_model}</span>
        )}
      </div>

      {group.triaged_note && (
        <>
          <Button variant="secondary" size="sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
            {open ? "Hide diagnosis" : "Read the diagnosis"}
          </Button>
          {open && <pre className="mon-page__triage-note">{group.triaged_note}</pre>}
        </>
      )}

      <ClaudeHandover group={group} requiresHuman={requiresHuman} onFlagChange={onFlagChange} />
    </Card>
  );
};

/** The auto-fix workflow (retail-circle.com/.github/workflows/auto-fix.yml) and its run history. */
const AUTO_FIX_RUNS_URL = "https://github.com/Retail-Circle/retail-circle.com/actions/workflows/auto-fix.yml";

/** Its cron is "25 * * * *" — UTC, so :25 UTC, whatever the reader's timezone. */
const nextAutoFixRun = (now = new Date()) => {
  const next = new Date(now);
  next.setUTCMinutes(25, 0, 0);
  if (next <= now) next.setUTCHours(next.getUTCHours() + 1);
  return next.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const HANDOVER_STEPS = ["Handed to Claude", "Fix written and tested", "Pull request opened", "Merged to staging"];
// How far along each auto_fix_state is; null = handed over, waiting for a run.
const HANDOVER_DONE = { proposed: 2, pr_open: 3, merged_staging: 4 };

/**
 * Hand an error to Claude to fix — a deliberate, confirmed button instead of
 * the old "A person handles this one" checkbox (Alex, 2026-10-14), saying what
 * will happen and where to watch it.
 *
 * Under the hood it is still the audited human flag: handing over sets
 * requires_human = false, which is the only thing the hourly auto-fix
 * workflow picks up. Taking it back sets it true again.
 */
const ClaudeHandover = ({ group, requiresHuman, onFlagChange }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  // The workflow only takes errors Claude has diagnosed as needing a change,
  // and never retries one it has already attempted.
  const notDiagnosedForFix = group.triaged_verdict !== "needs_human";
  const alreadyAttempted = !!group.auto_fix_state;
  const fromWebsite = group.app && group.app !== "api";

  const setFlag = async (next) => {
    setBusy(true);
    try {
      await onFlagChange(group.id, next);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (requiresHuman) {
    const reason = AUTO_FIX_PAUSED
      ? "Automatic fixing is paused at the moment, so there is nothing to hand this to yet. Claude still diagnoses every error."
      : notDiagnosedForFix
      ? "Claude's diagnosis did not find a code change to make, so there is nothing to hand over."
      : alreadyAttempted
        ? "Claude has already attempted this one — see above."
        : null;

    return (
      <div className="mon-page__handover">
        <Button variant="primary" size="sm" disabled={!!reason || busy} onClick={() => setConfirming(true)}>
          Hand to Claude to fix
        </Button>
        <p className="mon-page__handover-hint">
          {reason || "Claude diagnoses every error. Handing it over lets Claude also write and test a fix."}
        </p>

        <ConfirmDialog
          open={confirming}
          onClose={() => setConfirming(false)}
          onConfirm={() => setFlag(false)}
          loading={busy}
          title="Hand this error to Claude?"
          confirmLabel="Hand to Claude"
        >
          <div className="mon-page__handover-explain">
            <p>
              <strong>What Claude will do</strong>, at the next automatic run (about {nextAutoFixRun()}, then
              hourly — GitHub can start runs a few minutes late):
            </p>
            <ol>
              <li>Read its diagnosis and the code involved, and write a fix.</li>
              <li>
                Check the fix is small and only touches app code — never database changes, settings, deploy
                files or secrets.
              </li>
              <li>Run the tests for the code it changed (they must pass) and the full test suite (no worse than before).</li>
              <li>
                If everything passes, open a pull request and merge it to <strong>staging</strong>. If anything
                fails, stop and say why on this error.
              </li>
            </ol>
            <p>
              <strong>It never touches production.</strong> Releasing a fix to production is always your call.
            </p>
            {fromWebsite && (
              <p className="mon-page__handover-warn">
                This error came from the {APP_LABEL[group.app] || group.app} website. Claude can only change the
                backend (API) code, so if the cause is in the website&apos;s own code it will stop and say so.
              </p>
            )}
            <p>
              <strong>Where to follow it:</strong> right here on this error, step by step, and every run on{" "}
              <a href={AUTO_FIX_RUNS_URL} target="_blank" rel="noreferrer noopener">GitHub</a>. You can take it back
              at any time before it starts.
            </p>
          </div>
        </ConfirmDialog>
      </div>
    );
  }

  const done = group.auto_fix_state === "blocked" ? null : HANDOVER_DONE[group.auto_fix_state] ?? 1;

  return (
    <div className="mon-page__handover">
      <div className="mon-page__handover-head">
        <Badge tone={group.auto_fix_state === "blocked" ? "danger" : "accent"} size="sm" dot>
          {group.auto_fix_state === "blocked" ? "Claude stopped" : "Handed to Claude"}
        </Badge>
        {!group.auto_fix_state && (
          <span className="mon-page__handover-next">
            {AUTO_FIX_PAUSED
              ? "paused — nothing runs until automatic fixing is switched back on"
              : `next run about ${nextAutoFixRun()}`}
          </span>
        )}
      </div>

      {done !== null && (
        <ol className="mon-page__handover-steps">
          {HANDOVER_STEPS.map((label, i) => (
            <li
              key={label}
              className={i < done ? "is-done" : i === done ? "is-current" : undefined}
              aria-current={i === done ? "step" : undefined}
            >
              {label}
              {i === HANDOVER_STEPS.length - 1 && i < done ? " — verify there, then release to production yourself" : ""}
            </li>
          ))}
        </ol>
      )}

      {/* The pull request link is on the AutoFixBanner above, once there is one. */}
      <div className="mon-page__handover-links">
        <a href={AUTO_FIX_RUNS_URL} target="_blank" rel="noreferrer noopener">See the runs on GitHub →</a>
      </div>

      {!group.auto_fix_state && (
        <Button variant="secondary" size="sm" disabled={busy} onClick={() => setFlag(true)}>
          Take it back
        </Button>
      )}
    </div>
  );
};

const ErrorDetail = ({ loading, detail, onClose, onResolve, onIgnore, onReopen, onFlagChange }) => {
  const [note, setNote] = useState("");
  const [release, setRelease] = useState("");
  const [copied, setCopied] = useState(false);

  const group = detail?.group;
  const events = detail?.events || [];
  const bundle = detail?.debug_bundle || "";

  const copyBundle = async () => {
    try {
      await navigator.clipboard.writeText(bundle);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      /* clipboard blocked — ignore */
    }
  };

  return (
    <Card as="aside" surface="raised" padding="lg" className="mon-page__detail">
      <div className="mon-page__detail-head">
        {group && <AppBadge app={group.app} />}
        {group && <StateBadge state={group.effective_state || group.state} />}
        <IconButton label="Close" variant="ghost" onClick={onClose}>
          <Icon name="X" size="md" />
        </IconButton>
      </div>

      {loading ? (
        <StatePanel state="loading" compact title="Loading…" description={null} />
      ) : !detail || detail.error ? (
        <StatePanel state="error" compact title="Couldn't load this error." description={null} />
      ) : (
        <>
          <h2 className="mon-page__detail-title">{group.title}</h2>
          <div className="mon-page__detail-meta">
            <span>{group.type}</span>
            <span>· {group.environment}</span>
            <span>· {group.event_count} occurrences</span>
            <span>· {group.users_affected} users</span>
          </div>

          {group.page && (
            <div className="mon-page__kv">
              <span>Page</span>
              <code>{group.page}</code>
            </div>
          )}
          {events[0]?.interaction && (
            <div className="mon-page__kv">
              <span>Last interaction</span>
              <code>{events[0].interaction}</code>
            </div>
          )}
          {group.resolved_release && (
            <div className="mon-page__kv">
              <span>Fix build</span>
              <code>{group.resolved_release}</code>
            </div>
          )}
          {group.resolution_note && (
            <div className="mon-page__kv">
              <span>Note</span>
              <span>{group.resolution_note}</span>
            </div>
          )}

          {/*
            What the machine did, and what YOU need to do about it.

            Deliberately above the human actions: if an automated change has
            reached staging, that is the most important thing on this panel
            and must not sit below a fold. An automated change nobody notices
            is worse than no automation — the next person debugging has no
            idea a machine touched the code.
          */}
          <AutoFixBanner group={group} />

          <TriagePanel group={group} onFlagChange={onFlagChange} />

          <div className="mon-page__actions">
            <Button variant="primary" size="sm" onClick={() => onResolve(note, release)}>
              Mark resolved
            </Button>
            <Button variant="secondary" size="sm" onClick={onIgnore}>
              Ignore
            </Button>
            <Button variant="secondary" size="sm" onClick={onReopen}>
              Reopen
            </Button>
          </div>
          <p className="mon-page__handover-hint">
            Resolving adds this to the Incident log automatically — Claude writes up the cause and the lesson overnight.
          </p>
          <div className="mon-page__resolve-inputs">
            <Input
              size="md"
              placeholder="Fix build/release (git SHA) — optional"
              value={release}
              onChange={(e) => setRelease(e.target.value)}
            />
            <Input
              size="md"
              placeholder="Resolution note — optional"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <div className="mon-page__bundle-head">
            <h3>Debug bundle</h3>
            <Button variant="secondary" size="sm" onClick={copyBundle}>
              {copied ? "Copied ✓" : "Copy for Claude"}
            </Button>
          </div>
          <pre className="mon-page__bundle">{bundle}</pre>

          <h3 className="mon-page__events-head">Recent occurrences</h3>
          <div className="mon-page__events">
            {events.map((e) => (
              <div className="mon-page__event" key={e.id}>
                <div className="mon-page__event-top">
                  <span>{relTime(e.created_at)}</span>
                  <span className="mon-page__event-release">{e.release}</span>
                  {e.status ? <span className="mon-page__event-status">{e.status}</span> : null}
                </div>
                {e.interaction && <div className="mon-page__event-line">↳ {e.interaction}</div>}
                {e.request_url && <div className="mon-page__event-line mon-page__event-url">{e.request_url}</div>}
                {e.stack && <pre className="mon-page__event-stack">{e.stack}</pre>}
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

export default MonitoringPage;
